import { IGroupCallChanged, IIncomingCall } from '@gem/call/Model/callModel.types';
import { IOnlineUserData } from '@gem/models/userModel';

import {
  mapCalendarEventResponse,
  mapCalendarRealtimeEventAction,
} from 'APP/model/calendar/calendarModel.mapper';
import { IPushNotification } from 'APP/store/PushNotifications/PushNotifications.types';
import { wsAppEventsManager } from 'APP/wsApi/wsAppApi/WsAppApi';
import {
  CallIncomingRealtimeEvent,
  CallsActiveRealtimeEvent,
  CallSingleUpdateRealtimeEvent,
  ChatSingleUpdateRealtimeEvent,
  ChatStateChangedRealtimeEvent,
  ChatsUpdateRealtimeEvent,
  MessageDeletedRealtimeEvent,
  MessagesUpdateRealtimeEvent,
  PushNotificationRealtimeEvent,
  UsersStatusUpdateRealtimeEvent,
  UserStatusSingleUpdateRealtimeEvent,
  UserTypingMessageRealtimeEvent,
  MessageSentRealtimeEvent,
  CalendarUpdateRealtimeEvent,
  DeviceStatusUpdateEvent,
} from 'APP/wsApi/wsAppApi/types/realtime_event/realtime_event_pb';
import { untrustedWsAppEventsManager } from 'APP/wsApi/wsAppApi/untrustedWsAppApi/UntrustedWsAppApi';

import { ICalendarEventUpdate } from '../calendar/calendar.types';
import { mapGroupCounterView } from '../counter/counterModel.b2b.mapper';
import {
  IDeviceStatusUpdateEvent,
  IGroupCounterState,
  IGroupCounterView,
} from '../counter/counterModel.types';
import { IUserTypingMessageEvent, IMessageSentEvent } from '../group/groupModel.types';
import { mapIMessage } from '../message/messageModel.b2b.mapper';
import { IMessage, IMessageDeleteEvent } from '../message/messageModel.types';
import { mapIOnlineUserData } from '../user/userModel.b2b.mapper';
import {
  mapIDeviceStatusUpdate,
  mapIGroupCallChanged,
  mapIGroupCounterState,
  mapIIncomingCall,
  mapIPushNotification,
} from './realtimeEventModel.b2b.mapper';

export function subscribeCallsActiveRealtimeEvent(
  callback: (data: IGroupCallChanged[]) => void
): () => void {
  return wsAppEventsManager.on('callsActive', (data: CallsActiveRealtimeEvent) => {
    callback(data.items.map((item) => mapIGroupCallChanged(item)));
  });
}

export function subscribeDeviceStatusUpdatedRealtimeEvent(
  callback: (data: IDeviceStatusUpdateEvent) => void
): () => void {
  return wsAppEventsManager.on('deviceStatusUpdate', (data: DeviceStatusUpdateEvent) => {
    callback(mapIDeviceStatusUpdate(data));
  });
}

export function subscribeCallSingleUpdateRealtimeEvent(
  callback: (data: IGroupCallChanged) => void
): () => void {
  return wsAppEventsManager.on('callSingleUpdate', (data: CallSingleUpdateRealtimeEvent) => {
    callback(mapIGroupCallChanged(data.callUpdate!));
  });
}

export function subscribeCallIncomingRealtimeEvent(
  callback: (data: IIncomingCall) => void
): () => void {
  return wsAppEventsManager.on('callIncoming', (data: CallIncomingRealtimeEvent) => {
    console.log('INCOMING_CALL', '1 callIncoming event', data);
    callback(mapIIncomingCall(data));
  });
}

export function subscribeChatsUpdateRealtimeEvent(
  callback: (data: IGroupCounterView[]) => void
): () => void {
  return wsAppEventsManager.on('chatsUpdate', (data: ChatsUpdateRealtimeEvent) => {
    callback(data.items.map((item) => mapGroupCounterView(item)));
  });
}

export function subscribeChatSingleUpdateRealtimeEvent(
  callback: (data: IGroupCounterView) => void
): () => void {
  return wsAppEventsManager.on('chatSingleUpdate', (data: ChatSingleUpdateRealtimeEvent) => {
    callback(mapGroupCounterView(data.chatUpdate!));
  });
}

export function subscribeChatStateChangedRealtimeEvent(
  callback: (data: IGroupCounterState[]) => void
): () => void {
  return wsAppEventsManager.on('chatStateChanged', (data: ChatStateChangedRealtimeEvent) => {
    callback(data.items.map((item) => mapIGroupCounterState(item)));
  });
}

export function subscribeUsersStatusUpdateRealtimeEvent(
  callback: (data: IOnlineUserData[]) => void
): () => void {
  return wsAppEventsManager.on('usersStatusUpdate', (data: UsersStatusUpdateRealtimeEvent) => {
    callback(data.items.map((item) => mapIOnlineUserData(item)));
  });
}

export function subscribeUserStatusSingleUpdateRealtimeEvent(
  callback: (data: IOnlineUserData) => void
): () => void {
  return wsAppEventsManager.on(
    'userStatusSingleUpdate',
    (data: UserStatusSingleUpdateRealtimeEvent) => {
      callback(mapIOnlineUserData(data.userStatus!));
    }
  );
}

export function subscribePushNotificationRealtimeEvent(
  callback: (data: IPushNotification) => void
): () => void {
  return wsAppEventsManager.on('pushNotification', (data: PushNotificationRealtimeEvent) => {
    callback(mapIPushNotification(data));
  });
}

export function subscribeUpdateMessages(callback: (data: IMessage[]) => void): () => void {
  return wsAppEventsManager.on('messagesUpdate', (data: MessagesUpdateRealtimeEvent) => {
    callback(data.items.map(({ message }) => mapIMessage(message!)));
  });
}

export function subscribeDeleteMessages(callback: (data: IMessageDeleteEvent) => void): () => void {
  return wsAppEventsManager.on('messageDeleted', (data: MessageDeletedRealtimeEvent) => {
    callback({
      groupId: data.chatId.toString(),
      messageId: data.messageId.toString(),
    });
  });
}

export function subscribeUserTypingMessage(
  callback: (data: IUserTypingMessageEvent) => void
): () => void {
  return wsAppEventsManager.on('userTypingMessage', (data: UserTypingMessageRealtimeEvent) => {
    callback({
      ...data,
      groupId: data.chatId.toString(),
      userId: data.userId.toString(),
    });
  });
}

export function subscribeMessageSent(callback: (data: IMessageSentEvent) => void): () => void {
  return wsAppEventsManager.on('messageSent', (data: MessageSentRealtimeEvent) => {
    callback({
      groupId: data.chatId.toString(),
      userId: data.userId.toString(),
    });
  });
}

export function subscribeCalendarEventUpdate(
  callback: (data: ICalendarEventUpdate) => void
): () => void {
  return wsAppEventsManager.on('calendarEventUpdate', (data: CalendarUpdateRealtimeEvent) => {
    callback({
      event: data.event ? mapCalendarEventResponse(data.event) : null,
      action: mapCalendarRealtimeEventAction(data.action),
    });
  });
}

export function subscribeSessionInvalidated(callback: () => void): () => void {
  return wsAppEventsManager.on('sessionInvalidated', () => {
    callback();
  });
}

export function subscribeDeviceStatusUpdatedUntrustedRealtimeEvent(
  callback: (data: IDeviceStatusUpdateEvent) => void
): () => void {
  return untrustedWsAppEventsManager.on('deviceStatusUpdate', (data: DeviceStatusUpdateEvent) => {
    callback(mapIDeviceStatusUpdate(data));
  });
}
